/**
 * React Query hooks for video data fetching
 * Wraps existing video API with React Query for automatic caching, refetching, and state management
 */

import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback, useRef, useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
  fetchUserVideos,
  fetchPinnedVideos,
  fetchVideoById,
  pinVideo,
  unpinVideo,
  deleteVideoAPI,
  type VideoListParams,
  type VideoListResponse,
} from '@/api/videoApi';
import { videoKeys } from '../../../api/querry_keys';

/**
 * Common error rollback handler for mutations
 */
const handleMutationError = (queryClient: QueryClient) => {
  return (_err: unknown, _variables: unknown, context: { previousData?: Array<[any, unknown]> } | undefined) => {
    if (context?.previousData) {
      for (const [queryKey, data] of context.previousData) {
        queryClient.setQueryData(queryKey, data);
      }
    }
  };
};

/**
 * Common success handler for pin/unpin mutations
 */
const handlePinMutationSuccess = (queryClient: QueryClient) => {
  return () => {
    // Mark queries as stale but DON'T automatically refetch
    // Let the WebSocket handler control when to refetch to avoid duplicate requests
    queryClient.invalidateQueries({ 
      queryKey: videoKeys.pinnedVideos(true),
      refetchType: 'none' // Don't auto-refetch, just mark as stale
    }); // Admin
    queryClient.invalidateQueries({ 
      queryKey: videoKeys.pinnedVideos(false),
      refetchType: 'none' // Don't auto-refetch, just mark as stale
    }); // User
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'videos' && query.queryKey[1] === 'admin',
      refetchType: 'none' // Don't auto-refetch, just mark as stale
    });
  };
};

/**
 * Find a video by ID from query data
 */
const findVideoInQueryData = (data: unknown, videoId: string): any => {
  if (!data) return null;

  // Check infinite query pages
  if (typeof data === 'object' && 'pages' in data && Array.isArray(data.pages)) {
    for (const page of data.pages) {
      if (page.videos) {
        const video = page.videos.find((v: any) => v.id === videoId);
        if (video) return video;
      }
    }
  }

  // Check array data
  if (Array.isArray(data)) {
    return data.find((v: any) => v.id === videoId);
  }

  return null;
};

/**
 * Find a video across all queries
 */
const findVideoInQueries = (queries: Array<[any, unknown]>, videoId: string): any => {
  for (const [, data] of queries) {
    const video = findVideoInQueryData(data, videoId);
    if (video) return video;
  }
  return null;
};

/**
 * Create a pinned video object
 */
const createPinnedVideoObject = (video: any, userEmail: string) => ({
  ...video,
  is_pinned: true,
  pinned_at: new Date().toISOString(),
  pinned_by: userEmail
});

/**
 * Update pinned videos query by adding a video
 */
const updatePinnedVideosQuery = (
  queryClient: QueryClient,
  queryKey: any,
  oldData: unknown,
  videoId: string,
  userEmail: string,
  queriesToUpdate: Array<[any, unknown]>
) => {
  const pinnedArray = Array.isArray(oldData) ? oldData : [];

  // Find the video to pin
  const videoToPin = findVideoInQueries(queriesToUpdate, videoId);

  // Add to pinned if found and not already pinned
  if (videoToPin && !pinnedArray.some((v: any) => v.id === videoId)) {
    const pinnedVideo = createPinnedVideoObject(videoToPin, userEmail);
    queryClient.setQueryData(queryKey, [pinnedVideo, ...pinnedArray]);
  }
};

/**
 * Remove a video from pinned videos query
 */
const removePinnedVideoFromQuery = (
  queryClient: QueryClient,
  queryKey: any,
  oldData: unknown,
  videoId: string
) => {
  const pinnedArray = Array.isArray(oldData) ? oldData : [];
  queryClient.setQueryData(
    queryKey,
    pinnedArray.filter((v: any) => v.id !== videoId)
  );
};

/**
 * Update infinite query by setting pin status
 */
const updateInfiniteQueryPinStatus = (
  queryClient: QueryClient,
  queryKey: any,
  oldData: any,
  videoId: string,
  userEmail: string,
  isPinned: boolean
) => {
  const updatedPages = oldData.pages.map((page: any) => ({
    ...page,
    videos: page.videos ? page.videos.map((v: any) =>
      v.id === videoId
        ? {
            ...v,
            is_pinned: isPinned,
            pinned_at: isPinned ? new Date().toISOString() : undefined,
            pinned_by: isPinned ? userEmail : undefined
          }
        : v
    ) : []
  }));

  queryClient.setQueryData(queryKey, {
    pageParams: oldData.pageParams,
    pages: updatedPages
  });
};

/**
 * Update array query by setting pin status
 */
const updateArrayQueryPinStatus = (
  queryClient: QueryClient,
  queryKey: any,
  oldData: any[],
  videoId: string,
  userEmail: string,
  isPinned: boolean
) => {
  const updatedArray = oldData.map((v: any) =>
    v.id === videoId
      ? {
          ...v,
          is_pinned: isPinned,
          pinned_at: isPinned ? new Date().toISOString() : undefined,
          pinned_by: isPinned ? userEmail : undefined
        }
      : v
  );
  queryClient.setQueryData(queryKey, updatedArray);
};
/**
 * Hook for fetching user videos with infinite scroll pagination
 * Automatically caches and manages video list state
 */
interface UseUserVideosInfiniteParams extends VideoListParams {
  userEmail: string | null;
  enabled?: boolean;
}

export const useUserVideosInfinite = (params: UseUserVideosInfiniteParams) => {
  // Create a stable filter object for the query key
  const filters = useMemo(() => ({
    status: params.status,
    sort_by: params.sort_by,
    video_type: params.video_type,
    limit: params.limit,
    userEmail: params.userEmail || undefined,
  }), [params.status, params.sort_by, params.video_type, params.limit, params.userEmail]);

  return useInfiniteQuery({
    queryKey: videoKeys.infinite(filters),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await fetchUserVideos({
        ...params,
        offset: pageParam,
      });
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: VideoListResponse) => {
      const nextOffset = (lastPage.offset || 0) + (lastPage.limit || 12);
      return lastPage.has_more ? nextOffset : undefined;
    },
    enabled: !!params.userEmail && (params.enabled ?? true), // Only fetch when userEmail is available and gallery is enabled
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook for fetching pinned videos
 */
interface UsePinnedVideosParams {
  userEmail: string | null;
}

export const usePinnedVideos = (params: UsePinnedVideosParams) => {
  return useQuery({
    queryKey: videoKeys.pinnedVideos(false), // User = false for separate cache
    queryFn: async () => {
      const videos = await fetchPinnedVideos(); // Defaults to isAdmin: false
      return videos;
    },
    enabled: !!params.userEmail,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook for fetching a single video by ID
 */
export const useVideoById = (videoId: string | null) => {
  return useQuery({
    queryKey: videoKeys.detail(videoId || ''),
    queryFn: async () => {
      if (!videoId) throw new Error('Video ID is required');
      return await fetchVideoById(videoId);
    },
    enabled: !!videoId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook for pinning a video
 */
export const usePinVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ videoId }: { videoId: string; userEmail: string }) => {
      return await pinVideo(videoId);
    },
    onMutate: async ({ videoId, userEmail }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: videoKeys.all });

      // Snapshot the previous value for rollback and get queries to update
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: videoKeys.all });

      // Update each query optimistically
      for (const [queryKey, oldData] of queriesToUpdate) {
        if (!oldData) continue;

        // Handle pinned videos query (array)
        if (queryKey.includes('pinned')) {
          updatePinnedVideosQuery(queryClient, queryKey, oldData, videoId, userEmail, queriesToUpdate);
        }

        // Handle infinite query structure (videos list)
        if (typeof oldData === 'object' && 'pages' in oldData && Array.isArray(oldData.pages)) {
          updateInfiniteQueryPinStatus(queryClient, queryKey, oldData, videoId, userEmail, true);
        }

        // Handle array structure
        if (Array.isArray(oldData) && !queryKey.includes('pinned')) {
          updateArrayQueryPinStatus(queryClient, queryKey, oldData, videoId, userEmail, true);
        }
      }

      // Return context for rollback
      return { previousData: queriesToUpdate };
    },
    onError: handleMutationError(queryClient),
    onSuccess: handlePinMutationSuccess(queryClient),
  });
};

/**
 * Hook for unpinning a video
 */
export const useUnpinVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ videoId }: { videoId: string; userEmail: string }) => {
      return await unpinVideo(videoId);
    },
    onMutate: async ({ videoId, userEmail }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: videoKeys.all });

      // Snapshot the previous value for rollback and get queries to update
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: videoKeys.all });

      // Update each query optimistically
      for (const [queryKey, oldData] of queriesToUpdate) {
        if (!oldData) continue;

        // Handle pinned videos query (array) - remove from pinned
        if (queryKey.includes('pinned')) {
          removePinnedVideoFromQuery(queryClient, queryKey, oldData, videoId);
        }

        // Handle infinite query structure (videos list) - update pin status
        if (typeof oldData === 'object' && 'pages' in oldData && Array.isArray(oldData.pages)) {
          updateInfiniteQueryPinStatus(queryClient, queryKey, oldData, videoId, userEmail, false);
        }

        // Handle array structure
        if (Array.isArray(oldData) && !queryKey.includes('pinned')) {
          updateArrayQueryPinStatus(queryClient, queryKey, oldData, videoId, userEmail, false);
        }
      }

      // Return context for rollback
      return { previousData: queriesToUpdate };
    },
    onError: handleMutationError(queryClient),
    onSuccess: handlePinMutationSuccess(queryClient),
  });
};

/**
 * Helper function to filter videos from a page
 */
const filterVideosFromPage = (page: any, videoId: string) => ({
  ...page,
  videos: page.videos ? page.videos.filter((v: any) => v.id !== videoId) : []
});

/**
 * Helper function to remove video from query cache
 */
const removeVideoFromCache = (old: any, videoId: string) => {
  if (!old) return null;

  // Handle infinite query structure
  if (old.pages) {
    return {
      pageParams: old.pageParams,
      pages: old.pages.map((page: any) => filterVideosFromPage(page, videoId))
    };
  }

  // Handle array structure
  if (Array.isArray(old)) {
    return old.filter((v: any) => v.id !== videoId);
  }

  return old;
};

/**
 * Hook for deleting a video
 */
export const useDeleteVideo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ videoId, userEmail }: { videoId: string; userEmail: string }) => {
      return await deleteVideoAPI(videoId, userEmail);
    },
    onMutate: async ({ videoId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: videoKeys.all });

      // Snapshot the previous value for rollback and get queries to update
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: videoKeys.all });

      // Optimistically remove the video from all video queries
      for (const [queryKey, oldData] of queriesToUpdate) {
        const result = removeVideoFromCache(oldData, videoId);
        if (result !== null) {
          queryClient.setQueryData(queryKey, result);
        }
      }

      // Return context for rollback
      return { previousData: queriesToUpdate };
    },
    onError: handleMutationError(queryClient),
    // Always refetch after success or error to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.all });
      queryClient.refetchQueries({ queryKey: videoKeys.all });
    },
  });
};

/**
 * Helper hook that provides all video data with a compatible interface
 * This maintains backward compatibility with existing component structure
 */
export const useUserVideosWithPinned = (params: UseUserVideosInfiniteParams) => {
  const videosQuery = useUserVideosInfinite(params); // passes enabled through
  const pinnedQuery = usePinnedVideos({ userEmail: params.userEmail });
  const pinMutation = usePinVideo();
  const unpinMutation = useUnpinVideo();
  const deleteMutation = useDeleteVideo();

  // Flatten pages into single array (memoized to prevent re-creation on every render)
  const videos = useMemo(() =>
    videosQuery.data?.pages.flatMap(page => page.videos) ?? []
  , [videosQuery.data]);

  const pinnedVideos = useMemo(() =>
    pinnedQuery.data ?? []
  , [pinnedQuery.data]);

  // Get total counts from the first page
  const totalCount = videosQuery.data?.pages[0]?.total_count ?? 0;
  const totalUnfilteredCount = videosQuery.data?.pages[0]?.total_unfiltered_count ?? 0;

  // Track pending pin/unpin operations per video to prevent rapid fire requests
  const pendingOperationsRef = useRef<Map<string, { timer: ReturnType<typeof setTimeout>; type: 'pin' | 'unpin' }>>(new Map());

  // Get query client for manual cache updates
  const queryClient = useQueryClient();

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      for (const { timer } of pendingOperationsRef.current.values()) {
        clearTimeout(timer);
      }
      pendingOperationsRef.current.clear();
    };
  }, []);

  // Helper function to update cache immediately for instant UI feedback
  const updateCacheOptimistically = useCallback((videoId: string, userEmail: string, isPinning: boolean) => {
    const queriesToUpdate = queryClient.getQueriesData({ queryKey: videoKeys.all });
    for (const [queryKey, oldData] of queriesToUpdate) {
      if (!oldData) continue;

      // Handle pinned videos query (array)
      if (queryKey.includes('pinned')) {
        if (isPinning) {
          updatePinnedVideosQuery(queryClient, queryKey, oldData, videoId, userEmail, queriesToUpdate);
        } else if (Array.isArray(oldData)) {
          queryClient.setQueryData(queryKey, oldData.filter((v: any) => v.id !== videoId));
        }
      }

      // Handle infinite query structure (videos list)
      if (typeof oldData === 'object' && 'pages' in oldData && Array.isArray(oldData.pages)) {
        updateInfiniteQueryPinStatus(queryClient, queryKey, oldData, videoId, userEmail, isPinning);
      }

      // Handle array structure
      if (Array.isArray(oldData) && !queryKey.includes('pinned')) {
        updateArrayQueryPinStatus(queryClient, queryKey, oldData, videoId, userEmail, isPinning);
      }
    }
  }, [queryClient]);

  // Helper function to schedule debounced API call
  const scheduleApiCall = useCallback((
    videoId: string,
    userEmail: string,
    type: 'pin' | 'unpin',
    mutation: typeof pinMutation | typeof unpinMutation
  ) => {
    // Cancel any existing timer for this video
    const existing = pendingOperationsRef.current.get(videoId);
    if (existing) {
      clearTimeout(existing.timer);
    }

    // Create new timer - only the last call within 1 second will execute the API
    const timer = setTimeout(() => {
      if (!mutation.isPending) {
        mutation.mutate({ videoId, userEmail });
      }
      pendingOperationsRef.current.delete(videoId);
    }, 1000);

    pendingOperationsRef.current.set(videoId, { timer, type });
  }, []);

  // Create stable callback references (no dependencies to avoid infinite loops)
  const handleRefetch = useCallback(async () => {
    await Promise.all([
      videosQuery.refetch(),
      pinnedQuery.refetch()
    ]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePinVideo = useCallback((videoId: string, userEmail: string) => {
    // Immediately update cache for instant UI feedback
    updateCacheOptimistically(videoId, userEmail, true);
    
    // Schedule debounced API call
    scheduleApiCall(videoId, userEmail, 'pin', pinMutation);
  }, [updateCacheOptimistically, scheduleApiCall, pinMutation]);

  const handleUnpinVideo = useCallback((videoId: string, userEmail: string) => {
    // Immediately update cache for instant UI feedback
    updateCacheOptimistically(videoId, userEmail, false);
    
    // Schedule debounced API call
    scheduleApiCall(videoId, userEmail, 'unpin', unpinMutation);
  }, [updateCacheOptimistically, scheduleApiCall, unpinMutation]);

  const handleDeleteVideo = useCallback((videoId: string, userEmail: string) => {
    return deleteMutation.mutate({ videoId, userEmail });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => ({
    // Video data - videos now come with public URLs directly from backend
    videos: videos,
    pinnedVideos: pinnedVideos,

    // Loading states
    // Use isPending for initial load to prevent flash of empty state
    isLoading: videosQuery.isPending || videosQuery.isLoading || pinnedQuery.isPending || pinnedQuery.isLoading,
    isLoadingMore: videosQuery.isFetchingNextPage,

    // Error states
    error: videosQuery.error || pinnedQuery.error,

    // Pagination
    hasMore: videosQuery.hasNextPage ?? false,
    loadMore: videosQuery.fetchNextPage,

    // Counts
    totalCount,
    totalUnfilteredCount,

    // Refetch functions
    refetch: handleRefetch,

    // Mutations
    pinVideo: handlePinVideo,
    unpinVideo: handleUnpinVideo,
    deleteVideo: handleDeleteVideo,

    // Mutation states
    isPinning: pinMutation.isPending,
    isUnpinning: unpinMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Raw query objects for advanced use cases
    videosQuery,
    pinnedQuery,
  }), [
    videos,
    pinnedVideos,
    totalCount,
    totalUnfilteredCount,
    handleRefetch,
    handlePinVideo,
    handleUnpinVideo,
    handleDeleteVideo,
    pinMutation.isPending,
    unpinMutation.isPending,
    deleteMutation.isPending,
    videosQuery,
    pinnedQuery,
  ]);
};
